import { status } from '../../../../constants/attendanceStatus'

export type SortOrder = 'asc' | 'desc'

export interface SortState {
  sortType: string
  sortOrder: SortOrder
}

export const nextSortState = (
  current: SortState,
  newType: string
): SortState => {
  if (current.sortType === newType) {
    return {
      sortType: newType,
      sortOrder: current.sortOrder === 'asc' ? 'desc' : 'asc',
    }
  }
  return { sortType: newType, sortOrder: 'asc' }
}

/**
 * Rank used to group Roster / Roster Spares rows by availability, so the
 * players who said they're coming are always visible first:
 *
 *   0 — Attending ("yes")
 *   1 — Maybe / Not attending (they responded, just not a yes)
 *   2 — No response yet (null/undefined attendance_status)
 *
 * Anything unrecognized ranks with "no response" rather than jumping the list.
 */
export const availabilityRank = (attendanceStatus?: number | null): number => {
  if (attendanceStatus === status.ATTENDING) {
    return 0
  }
  if (
    attendanceStatus === status.MAYBE ||
    attendanceStatus === status.NOT_ATTENDING
  ) {
    return 1
  }
  return 2
}

/**
 * Wrap a column comparator so availability always wins: attending rows first,
 * then responded-but-not-attending, then unanswered. Ties fall through to the
 * caller's existing sort (name / number / position), keeping that behaviour
 * intact within each availability group.
 */
export const withAvailabilityFirst =
  <T extends { attendance_status?: number | null }>(
    compare: (a: T, b: T) => number
  ) =>
  (a: T, b: T): number => {
    const rankDiff =
      availabilityRank(a.attendance_status) -
      availabilityRank(b.attendance_status)
    return rankDiff !== 0 ? rankDiff : compare(a, b)
  }
